import 'twin.macro'
import { GetServerSideProps } from 'next'
import { useTranslation } from '@i18n'
import { TagArticles, TagArticles_tagArticles_articles } from '@apollo-types/TagArticles'
import React from 'react'
import ArticleCard from './article-card'
import TagStyle from './style'
import getTagArticlesResult from './_core/getTagArticlesResult'
import ErrorNoInternet from '@components/ErrorNoInternet'
import Pagination from '../search/pagination'
import ShowADScript from '@components/ShowADScript'
import { dispatchCustomEvent } from '@libs/customEvents'
import { tagUrl } from '@libs/appUrl'
import TagHead from './TagHead'

interface TagArticlesResultProps {
  tag: string
  currentPage: number // 1~XXX
  totalCount: number
  limit: number
  articles: TagArticles_tagArticles_articles[]
  pageUrl: (page: number) => string
}

const TagArticlesResult = (props: TagArticlesResultProps) => {
  const { t } = useTranslation('tag')
  const { articles, currentPage, totalCount, pageUrl, tag, limit } = props

  return articles.length > 0 ? (
    <>
      <TagStyle.ResultContainer>
        {articles.map((article, index) => {
          const cardIndex = (currentPage - 1) * limit + index
          return (
            <TagStyle.CardWrapper key={article.id}>
              <ArticleCard article={article} />
            </TagStyle.CardWrapper>
          )
        })}
      </TagStyle.ResultContainer>
      <TagStyle.Footer>
        <Pagination currentPage={currentPage} totalCount={totalCount} pageUrl={pageUrl} />
      </TagStyle.Footer>
    </>
  ) : (
    <TagStyle.EmptyResultContainer>
      <ErrorNoInternet />
      <p>{t(['notFound', '沒有相符文章'])}</p>
    </TagStyle.EmptyResultContainer>
  )
}

const TagHeader = (props) => {
  const { t } = useTranslation('tag')
  const { totalCount, tag } = props
  return (
    <TagStyle.Header>
      <span className="tag-name">{tag}</span>
      <span>{`${t(['total', '共'])} ${totalCount} ${t(['articles', '則文章'])}`}</span>
    </TagStyle.Header>
  )
}

interface TagProps {
  tagArticlesResult: {
    data: TagArticles
  }
  tag: string
  currentPage: number
  limit: number
  referrer?: string
  fromPage?: string
  isApp?: boolean
}

const Tag = (props: TagProps) => {
  const { tagArticlesResult, tag, currentPage, limit, isApp } = props
  const defaultPublishDate = '2021-06-01T00:00:00.000Z' // 標籤頁上線時間
  const articles = tagArticlesResult?.data?.tagArticles?.articles ?? []
  const totalCount = tagArticlesResult?.data?.tagArticles?.totalCount ?? 10

  const pageUrl = (page: number) => tagUrl(tag, page)

  return (
    <>
      <TagHead tag={tag} currentPage={currentPage} articles={articles} />
      <ShowADScript />
      <TagStyle.Layout isApp={isApp}>
        <TagStyle.Container>
          <TagHeader totalCount={totalCount} tag={tag} />
          <TagArticlesResult
            tag={tag}
            articles={articles}
            currentPage={currentPage}
            totalCount={totalCount}
            limit={limit}
            pageUrl={pageUrl}
          />
        </TagStyle.Container>
      </TagStyle.Layout>
    </>
  )
}

export default Tag

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const query = ctx.query as Record<string, string>
  const tag = query.tag ?? ''
  const currentPage = parseInt(query.page, 10) || 1
  const limit = 10

  // 如果網址是 /tag?tag=:tag
  // 轉址到 /tag/:tag
  if (tag && !ctx.params?.tag) {
    return {
      props: {},
      redirect: { destination: tagUrl(tag, currentPage), permanent: true },
    }
  }

  const tagArticlesResult = tag ? await getTagArticlesResult({ ctx, tag, currentPage, limit }) : {}
  const referrer = ctx?.req?.headers?.referer
  const [, searchParams = ''] = ctx.req.url.split('?')
  const fromPage = require('qs').parse(searchParams, { allowDots: true }).fromPage ?? {}

  if (referrer && !fromPage.pageUrl) {
    fromPage.pageUrl = referrer
  }

  return {
    props: { tagArticlesResult, tag, currentPage, fromPage, limit },
  }
}
